import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthProvider';
import { SecurityService } from '@/utils/securityService';
import { TwoFactorAuth } from '@/types/security';
import { supabase } from '@/integrations/supabase/client';

interface TwoFactorAuthContextType {
  requires2FA: boolean;
  is2FAVerified: boolean;
  loading: boolean;
  verify2FA: (code: string) => Promise<boolean>;
  verifyBackupCode: (code: string) => Promise<boolean>;
  skip2FA: () => void;
  reset2FA: () => void;
}

const TwoFactorAuthContext = createContext<TwoFactorAuthContextType | undefined>(undefined);

export const useTwoFactorAuth = () => {
  const context = useContext(TwoFactorAuthContext);
  if (context === undefined) {
    throw new Error('useTwoFactorAuth must be used within a TwoFactorAuthProvider');
  }
  return context;
};

export const TwoFactorAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [requires2FA, setRequires2FA] = useState(false);
  const [is2FAVerified, setIs2FAVerified] = useState(false);
  const [twoFactorStatus, setTwoFactorStatus] = useState<TwoFactorAuth | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasCheckedSession, setHasCheckedSession] = useState(false);

  useEffect(() => {
    if (user && !hasCheckedSession) {
      setHasCheckedSession(true);
      
      // Check if 2FA is already verified for this session
      const session2FAVerified = sessionStorage.getItem('2fa_verified');
      const sessionUserId = sessionStorage.getItem('2fa_user_id');
      
      if (session2FAVerified === 'true' && sessionUserId === user.id) {
        // 2FA already verified for this session - no loading needed
        setRequires2FA(false);
        setIs2FAVerified(true);
        setLoading(false);
        // Check status in background without loading state
        check2FAStatusBackground();
      } else {
        // When user logs in, start with secure state (assume 2FA is required until proven otherwise)
        setRequires2FA(true);
        setIs2FAVerified(false);
        setLoading(true);
        check2FAStatus();
      }
    } else if (!user) {
      // Reset 2FA state when user logs out
      setRequires2FA(false);
      setIs2FAVerified(false);
      setTwoFactorStatus(null);
      setLoading(false);
      setHasCheckedSession(false);
      // Clear session storage
      sessionStorage.removeItem('2fa_verified');
      sessionStorage.removeItem('2fa_user_id');
    }
  }, [user, hasCheckedSession]);

  const check2FAStatus = async () => {
    try {
      setLoading(true);
      const status = await SecurityService.twoFactor.get2FAStatus();
      setTwoFactorStatus(status);
      
      if (status.enabled) {
        // Check if already verified in this session
        const session2FAVerified = sessionStorage.getItem('2fa_verified');
        const sessionUserId = sessionStorage.getItem('2fa_user_id');
        
        if (session2FAVerified === 'true' && sessionUserId === user?.id) {
          setRequires2FA(false);
          setIs2FAVerified(true);
        } else {
          setRequires2FA(true);
          setIs2FAVerified(false);
        }
      } else {
        setRequires2FA(false);
        setIs2FAVerified(true);
      }
    } catch (error) {
      console.error('Error checking 2FA status:', error);
      // If we can't check 2FA status, assume it's not required
      setRequires2FA(false);
      setIs2FAVerified(true);
    } finally {
      setLoading(false);
    }
  };

  const check2FAStatusBackground = async () => {
    try {
      const status = await SecurityService.twoFactor.get2FAStatus();
      setTwoFactorStatus(status);
      
      // Only update state if 2FA status has changed
      if (status.enabled && !requires2FA) {
        setRequires2FA(true);
        setIs2FAVerified(false);
      } else if (!status.enabled && requires2FA) {
        setRequires2FA(false);
        setIs2FAVerified(true);
      }
    } catch (error) {
      console.error('Error checking 2FA status in background:', error);
    }
  };

  const verify2FA = async (code: string): Promise<boolean> => {
    try {
      if (!twoFactorStatus?.secret) {
        throw new Error('2FA secret not available');
      }

      // For demo purposes, we'll use a simple verification
      // In production, you would use a proper TOTP library like 'otplib'
      const isValid = /^\d{6}$/.test(code);
      
      if (isValid) {
        setIs2FAVerified(true);
        setRequires2FA(false);
        
        // Store verification in session storage
        if (user) {
          sessionStorage.setItem('2fa_verified', 'true');
          sessionStorage.setItem('2fa_user_id', user.id);
        }
        
        // Update last verification time
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            await supabase
              .from('user_profiles')
              .update({
                last_2fa_verification: new Date().toISOString(),
                updated_at: new Date().toISOString()
              })
              .eq('user_id', user.id);
          }
        } catch (error) {
          console.warn('Could not update last verification time:', error);
        }
        
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Error verifying 2FA code:', error);
      return false;
    }
  };

  const verifyBackupCode = async (code: string): Promise<boolean> => {
    try {
      const isValid = await SecurityService.twoFactor.verifyBackupCode(code);
      if (isValid) {
        setIs2FAVerified(true);
        setRequires2FA(false);
        
        // Store verification in session storage
        if (user) {
          sessionStorage.setItem('2fa_verified', 'true');
          sessionStorage.setItem('2fa_user_id', user.id);
        }
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error verifying backup code:', error);
      return false;
    }
  };

  const skip2FA = () => {
    // Only allow skipping if 2FA is not enabled
    if (!twoFactorStatus?.enabled) {
      setIs2FAVerified(true);
      setRequires2FA(false);
    }
  };

  const reset2FA = () => {
    setRequires2FA(false);
    setIs2FAVerified(false);
    setTwoFactorStatus(null);
    // Clear session storage
    sessionStorage.removeItem('2fa_verified');
    sessionStorage.removeItem('2fa_user_id');
  };

  return (
    <TwoFactorAuthContext.Provider
      value={{
        requires2FA,
        is2FAVerified,
        loading,
        verify2FA,
        verifyBackupCode,
        skip2FA,
        reset2FA,
      }}
    >
      {children}
    </TwoFactorAuthContext.Provider>
  );
}; 